import type {
  Location,
  PinIconInterface,
  PinSymbolInterface,
} from '../../GoogleMap.types';
import { isValidZoom } from './utils';

export const createSdkMethods = (
  mapRef: React.RefObject<google.maps.Map>,
  updateZoom: (zoom: number) => void,
  updateCenter: (center: google.maps.LatLngLiteral) => void,
) => {
  return {
    setMapCenter: (longitude: number, latitude: number) => {
      const center = { lat: latitude, lng: longitude };

      if (mapRef.current && center) {
        // Use direct map methods without triggering React state updates
        // This prevents re-renders during programmatic changes
        mapRef.current.setCenter(center);
        updateCenter(center);
      }

      return Promise.resolve();
    },

    setMapZoom: (zoom: number) => {
      if (mapRef.current && isValidZoom(zoom)) {
        // Use direct map methods without triggering React state updates
        mapRef.current.setZoom(zoom);
        updateZoom(zoom);
      }

      return Promise.resolve();
    },

    fitBounds: (bounds: {
      north: number;
      south: number;
      east: number;
      west: number;
    }) => {
      if (mapRef.current) {
        const googleBounds = new google.maps.LatLngBounds(
          { lat: bounds.south, lng: bounds.west },
          { lat: bounds.north, lng: bounds.east },
        );

        // Use direct map methods without triggering React state updates
        mapRef.current.fitBounds(googleBounds);
        const center = mapRef.current.getCenter();
        const zoom = mapRef.current.getZoom();
        if (center && isValidZoom(zoom)) {
          updateZoom(zoom!);
          updateCenter({ lat: center.lat(), lng: center.lng() });
        }
      }
      return Promise.resolve();
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setMarkers: (locations: Array<Location>) => {
      // TODO: Implement marker setting logic
      return Promise.resolve();
    },

    getVisibleMarkers: (locations: Array<Location>) => {
      if (mapRef.current) {
        const bounds = mapRef.current.getBounds();
        if (bounds) {
          const visibleMarkers = locations
            .filter(marker => {
              const position = new google.maps.LatLng(
                marker.latitude,
                marker.longitude,
              );
              return bounds.contains(position);
            })
            .map(marker => ({
              title: marker.title,
              location: {
                latitude: marker.latitude,
                longitude: marker.longitude,
              },
            }));
          return Promise.resolve(visibleMarkers);
        }
      }

      return Promise.resolve(
        locations.map(loc => ({
          title: loc.title,
          location: {
            latitude: loc.latitude,
            longitude: loc.longitude,
          },
        })),
      );
    },

    setMarkerIcon: (_updateData: {
      coordinates: {
        latitude: Location['latitude'];
        longitude: Location['longitude'];
      };
      iconOptions: PinIconInterface | PinSymbolInterface;
      locations: Array<Location>;
    }) => {
      // TODO implement setMarkerIcon
      return Promise.resolve();
    },
  };
};
